import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { mapToVoid } from '@examdojo/core/rxjs';
import { UserUIModel } from '@examdojo/models/user';
import { UserQuery } from '@examdojo/user';
import { UserSettingsQuery } from '@examdojo/user-settings';
import { first, switchMap, tap } from 'rxjs';
import { UserMarksHttpService } from './user-marks-http.service';
import { UserMarksStore } from './user-marks.store';

@Injectable({ providedIn: 'root' })
export class UserMarksService {
  constructor(
    private readonly httpService: UserMarksHttpService,
    private readonly store: UserMarksStore,
    private readonly errorHandler: ErrorHandlerService,
    private readonly userQuery: UserQuery,
    private readonly userSettingsQuery: UserSettingsQuery,
  ) {}

  fetchLastWeekUserMarks() {
    return this.userSettingsQuery.timezone$.pipe(
      first(),
      switchMap((timezone) => this.httpService.fetchLastWeekDailyMarks(timezone ?? 'UTC')),
      tap((data) => this.store.updateStore((state) => ({ ...state, dailyMarks: data }))),
      this.errorHandler.setHttpErrorMetadata({ entity: 'examdojo.entity.user_marks' }),
      mapToVoid(),
    );
  }

  getUserSpentPointsAndLimits() {
    return this.userQuery.active$.pipe(
      first(Boolean),
      switchMap((active) => this.fetchUserMarksAttemptsAndLimits(active.id)),
      mapToVoid(),
    );
  }

  fetchTotalMarksEarned() {
    return this.userQuery.active$.pipe(
      first(Boolean),
      switchMap((active) => this.httpService.getTotalMarksEarned(active.id)),
      tap((totalMarks) => this.store.updateStore((state) => ({ ...state, totalMarksEarned: totalMarks }))),
      this.errorHandler.setHttpErrorMetadata({ entity: 'examdojo.entity.user_marks' }),
      mapToVoid(),
    );
  }

  private fetchUserMarksAttemptsAndLimits(userId: UserUIModel['id']) {
    return this.httpService.getUserMarksAttemptsAndLimits(userId).pipe(
      tap((data) =>
        this.store.updateStore((state) => ({
          ...state,
          userMarksAttemptsAndLimits: {
            point_limit: data.point_limit,
            spent_points: data.spent_points ?? 0,
            next_refresh: data.next_refresh,
            next_refresh_amount: data.next_refresh_amount,
          },
        })),
      ),
      this.errorHandler.setHttpErrorMetadata({ entity: 'examdojo.entity.user_marks' }),
    );
  }
}
